import clsx from 'clsx';
import { useCourse } from 'core/lite/hooks/useCourse';
import DialogAssignStudents from 'core/modules/components/dialogs/DialogAssignStudents/DialogAssignStudents';
import StudentsGradesDropdownMenu from 'core/modules/components/dropdowns/StudentsGradesDropdownMenu/StudentsGradesDropdownMenu';
import { uniqueId } from 'lodash';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Avatar, Icon } from 'ui';
import { useAvatarsList } from './useAvatarsList';

// showRestStudents
// si esta propiedad viene a true, se mostrará los primeros avatares, pero en la buble se mostrará un +
// y el resto de avatares que quedan por mostrarse, si viene a false, se mostrara en la buble, el total de avatares que hay

// Constante de módulo y no `[]` en la firma: un array nuevo en cada render invalidaría el useMemo.
const NO_AVATARS = [];

function AvatarsList({
  align,
  allStudents,
  ariaLabel,
  avatars = NO_AVATARS,
  courseGuid,
  isScore = false,
  lessonItemGuid,
  maxAvatars = 2,
  onClick,
  onSaveAssignedStudents,
  opacity,
  scoreScale,
  showDropdown = true,
  showRestStudents,
  size = 'sm',
  withArrow = false,
}) {
  const { data: course } = useCourse(courseGuid);
  const isReadOnly = course?.isReadOnly ?? false;

  const {
    handleClickComponent,
    handleCloseAssignStudentsDialog,
    handleCloseDropdown,
    handleSaveAssignedStudents,
    getMainAriaLabel,
    anchorEl,
    loading,
    openAssignStudentsDialog,
    openDropdown,
  } = useAvatarsList({
    allStudents,
    ariaLabel,
    courseGuid,
    lessonItemGuid,
    onClick,
    onSaveAssignedStudents,
    showDropdown,
    avatars,
  });

  function renderNumberBubble(count) {
    return (
      <div className="avatars-list__count-bubble">
        <span className="avatars-list__count-bubble-text">{count}</span>
      </div>
    );
  }

  function renderAvatars(avatars = []) {
    return (
      <div className="avatars-list__avatars-container">
        <div className={`avatars-list__avatars avatars-list__avatars--${avatars.length <= maxAvatars ? avatars.length : 'more'}`}>
          {avatars.slice(0, maxAvatars).map((item) => (
            <div key={uniqueId()} className="horizontal-students-result__student">
              <Avatar image={item.thumbnail || item.avatar} size={size} />
            </div>
          ))}
          {avatars.length > maxAvatars && renderNumberBubble(showRestStudents ? `+${avatars.length - maxAvatars}` : avatars.length)}
        </div>
        {avatars.length > 0 && withArrow && (
          <div className="avatars-list__arrow">
            <Icon name="arrow-down" />
          </div>
        )}
      </div>
    );
  }

  const avatarsMemoized = useMemo(() => renderAvatars(avatars), [avatars, maxAvatars, size, withArrow, showRestStudents]);

  return (
    <>
      <button
        type="button"
        className={`button-accessibility-only ${clsx(
          'avatars-list',
          { 'avatars-list--opacity': opacity, 'avatars-list--show-rest': showRestStudents },
          `avatars-list--size-${size}`,
          `avatars-list--align-${align}`
        )}`}
        onClick={handleClickComponent}
        aria-label={getMainAriaLabel()}
      >
        {avatarsMemoized}

        <StudentsGradesDropdownMenu
          anchorEl={anchorEl}
          isScore={isScore}
          onClose={handleCloseDropdown}
          open={openDropdown}
          scoreScale={scoreScale}
          users={avatars}
        />
      </button>

      <DialogAssignStudents
        isReadOnly={isReadOnly}
        loading={loading}
        onCancel={handleCloseAssignStudentsDialog}
        onSuccess={handleSaveAssignedStudents}
        open={openAssignStudentsDialog}
        students={allStudents}
      />
    </>
  );
}

AvatarsList.propTypes = {
  align: PropTypes.oneOf(['left', 'center', 'right']),
  allStudents: PropTypes.array,
  // Nombre del botón con el contexto del consumidor; sin él, «N estudiantes asignados: …».
  ariaLabel: PropTypes.string,
  avatars: PropTypes.array,
  courseGuid: PropTypes.string,
  isScore: PropTypes.bool,
  lessonItemGuid: PropTypes.string,
  maxAvatars: PropTypes.number,
  onClick: PropTypes.func,
  onSaveAssignedStudents: PropTypes.func,
  opacity: PropTypes.bool,
  scoreScale: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showDropdown: PropTypes.bool,
  showRestStudents: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md']),
  withArrow: PropTypes.bool,
};

export default AvatarsList;
